/**
 * analytics.ts — GA4 event tracking helpers for InviteCloud Admin Portal.
 *
 * Design rules:
 *   - All gtag() calls go through this module. Never call window.gtag() directly
 *     from components — that makes tracking impossible to grep/audit/test.
 *   - Uses the native `arguments` object (NOT spread `...args`) when pushing to
 *     dataLayer. GA4 silently drops events if you push a plain Array instead of
 *     the Arguments object.
 *   - All functions are no-ops when window.gtag is not loaded (dev / test / blocked).
 *   - Measurement ID is read from VITE_GA_MEASUREMENT_ID at runtime.
 *
 * Usage:
 *   import { trackEvent, trackPageView } from '@/src/infrastructure/lib/analytics';
 *   trackEvent('ADMIN_LOGIN', 'auth');
 */

// ── Type declarations ──────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// ── Product event names (matches requirements doc/admin specifications) ─────

export type AdminProductEventName =
  | 'ADMIN_LOGIN'
  | 'ADMIN_LOGOUT'
  | 'ADMIN_DASHBOARD_VIEW'
  | 'ADMIN_ANALYTICS_VIEW'
  | 'INVITATION_SUSPENDED'
  | 'INVITATION_RESTORED'
  | 'TEMPLATE_CREATED'
  | 'TEMPLATE_UPDATED';

// ── Internal helper ────────────────────────────────────────────────────────

/** Hosts allowed to send analytics. Override with VITE_GA_ALLOWED_HOSTS (comma-separated). */
const DEFAULT_ANALYTICS_HOSTS = ['admin.invitecloud.in'];

export function isAnalyticsHost(hostname: string): boolean {
  const override = (import.meta.env.VITE_GA_ALLOWED_HOSTS as string | undefined)?.trim();
  const allowed = override ? override.split(',').map((h) => h.trim()) : DEFAULT_ANALYTICS_HOSTS;
  return allowed.includes(hostname);
}

function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

// ── Bootstrap ──────────────────────────────────────────────────────────────

/**
 * Load gtag.js and configure GA4 — but ONLY when a real Measurement ID is set.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  // localhost / *.pages.dev previews must not report into the production GA4 property.
  if (!isAnalyticsHost(window.location.hostname)) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!measurementId || !/^G-[A-Z0-9]+$/i.test(measurementId)) return;
  if (isGtagAvailable()) return; // HMR guard

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Associate the logged-in user ID with the GA4 session for cross-device tracking.
 * Call with the user's stable database ID when signed in, or null when signed out.
 */
export function setUserId(userId: string | null): void {
  if (!isGtagAvailable()) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!measurementId) return;

  window.gtag('config', measurementId, {
    user_id: userId ?? undefined,
  });
}

/**
 * Track a virtual page view. Must be an explicit `page_view` event: re-`config`ing with
 * page_path does not send a hit once `send_page_view` is false.
 */
export function trackPageView(path: string, title?: string): void {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  });
}

/**
 * Track an admin event.
 */
export function trackEvent(
  name: AdminProductEventName,
  category: string,
  label?: string,
  value?: number,
): void {
  if (!isGtagAvailable()) return;

  const params: Record<string, unknown> = { event_category: category };
  if (label !== undefined) params.event_label = label;
  if (value !== undefined) params.value = value;

  window.gtag('event', name, params);
}

/**
 * Forward a Sentry exception to GA4.
 */
export function trackSentryException(description: string, sentryEventId: string): void {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'exception', {
    description,
    fatal: true,
    sentry_event_id: sentryEventId,
  });
}
